/* global Config */

// Vendor Components
import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from 'react-router-dom';
import { persistStore } from 'redux-persist';
import LocationLogger from './location-logger';

// Styles
import './styles/styles.scss';

// Core
import store from './store';

// Components
import ChangePassword from './components/app/change-password';
import DashboardAudits from './components/layout/dashboard-audits';
import DashboardEnhancedView from './components/layout/dashboard-enhanced-view';
import DashboardFlaggedMetadataItems from './components/layout/dashboard-flagged-metadata-items';
import DashboardHealth from './components/layout/dashboard-health';
import DashboardMapExternalNetworks from './components/layout/dashboard-map-external-networks';
import DashboardMassActionResults from './components/layout/dashboard-mass-action-results';
import DashboardMediaItems from './components/layout/dashboard-media-items';
import DashboardMoviesAndPrograms from './components/layout/dashboard-movies-and-programs';
import DashboardNetworks from './components/layout/dashboard-networks';
import DashboardProviders from './components/layout/dashboard-providers';
import DashboardRoles from './components/layout/dashboard-roles';
import DashboardRules from './components/layout/dashboard-rules';
import DashboardSelectNetworkForDisplayPathModifiers from './components/layout/dashboard-select-network-for-display-path-modifiers';
import DashboardSelectNetworkForDisplayPaths from './components/layout/dashboard-select-network-for-display-paths';
import DashboardUsers from './components/layout/dashboard-users';
import DashboardViolations from './components/layout/dashboard-violations';
import DashboardVfb from './components/layout/dashboard-vfb';
import Loading from './components/core/loading';
import Main from './components/layout/main';
import NoMatch from './components/layout/no-match';
import ResetPassword from './components/app/reset-password';
import EarlIframeContainerComp from './components/app/earl-iframe-container';

const App = () => {
  const [rehydrated, setRehydrated] = useState(false);

  useEffect(() => {
    persistStore(store, null, () => {
      setRehydrated(true);
    });
  }, []);

  if (!rehydrated) {
    return <Loading loading={true} />;
  }

  const baseUrl = Config.base.baseUrl || '/';

  return (
    <Provider store={store}>
      <Router basename={baseUrl}>
        <LocationLogger />
        <Routes>
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/" element={<Main />}>
            <Route index={true} element={<DashboardMediaItems />} />
            <Route path="media-items/*" element={<DashboardMediaItems />} />
            <Route path="movies-and-programs/*" element={<DashboardMoviesAndPrograms />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="admin/health" element={<DashboardHealth />} />
            <Route path="admin/users/*" element={<DashboardUsers />} />
            <Route path="admin" element={<DashboardUsers />} />
            <Route path="roles/*" element={<DashboardRoles />} />
            <Route path="networks/*" element={<DashboardNetworks />} />
            <Route path="map-external-networks" element={<DashboardMapExternalNetworks />} />
            <Route path="providers/*" element={<DashboardProviders />} />
            <Route path="rules/*" element={<DashboardRules />} />
            <Route path="investigation/mass-action-results/*" element={<DashboardMassActionResults />} />
            <Route path="investigation/flagged-metadata/*" element={<DashboardFlaggedMetadataItems />} />
            <Route path="investigation/enhanced-view/*" element={<DashboardEnhancedView />} />
            <Route path="investigation/violations/*" element={<DashboardViolations />} />
            <Route path="audits/*" element={<DashboardAudits />} />
            <Route path="display-paths/*" element={<DashboardSelectNetworkForDisplayPaths />} />
            <Route path="display-path-modifiers/*" element={<DashboardSelectNetworkForDisplayPathModifiers />} />
            <Route path="vfb/*" element={<DashboardVfb />} />
            <Route path="admin/earl" element={<EarlIframeContainerComp />} />
            <Route path="*" element={<NoMatch />} />
          </Route>
        </Routes>
        <Outlet />
      </Router>
    </Provider>
  );
};

export default App;
